'use client'

import { useSession } from 'next-auth/react'
import { Button } from 'ui/button'
import { toast } from 'sonner'
import { useRouter, usePathname } from 'next/navigation'
import { useState } from 'react'
import { createCheckoutSessionAction } from 'components/Premium/UserPremiumActions'
import PremiumCancelationDialog from './PremiumCancelationDialog'
import LoadingAnimation from 'components/layout/LoadingAnimation'

interface SubmitButtonProps {
    status?: string
    buttonText: string
    notLoggedIn: string
    userPremiumUnsubscribeConfirmationTitle: string
    areYouSureYouWantToCancelYourPremiumSubscriptionYouWillLoseAccessToAllPremiumFeaturesAtTheEndOfYourCurrentSubscriptionPeriod: string
    keepPremium: string
    cancelPremium: string
    premiumSubscriptionCancelledSuccessfully: string
    paymentProviderRedirect: string
    errorResumingSubscription: string
    errorSubscribing: string
    errorUnsubscribing: string
    lang: string
}

const PremiumToggleButton: React.FC<SubmitButtonProps> = ({
    status,
    buttonText,
    notLoggedIn,
    userPremiumUnsubscribeConfirmationTitle,
    areYouSureYouWantToCancelYourPremiumSubscriptionYouWillLoseAccessToAllPremiumFeaturesAtTheEndOfYourCurrentSubscriptionPeriod,
    keepPremium,
    cancelPremium,
    premiumSubscriptionCancelledSuccessfully,
    paymentProviderRedirect,
    errorResumingSubscription,
    errorSubscribing,
    errorUnsubscribing,
    lang
}) => {
    const { data: session } = useSession()
    const router = useRouter()
    const pathname = usePathname()
    const [isLoading, setIsLoading] = useState(false)
    const [showCancelationDialog, setShowCancelationDialog] = useState(false)

    // Get the full URL including origin
    const getFullUrl = () => {
        if (typeof window !== 'undefined') {
            return window.location.href
        }
        return pathname // Fallback to just pathname if window is not available
    }

    const openPortal = async (): Promise<boolean> => {
        const res = await fetch('/api/payments/create-portal-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ returnPath: `/${lang}/premium` }),
            credentials: 'include'
        })
        const j = await res.json()
        if (j?.data?.redirect_url) {
            toast.success(paymentProviderRedirect)
            router.push(j.data.redirect_url)
            return true
        }
        toast.error(j?.error || 'Failed to open subscription management')
        return false
    }

    const togglePremiumSubscription = async (status?: string): Promise<void> => {
        if (isLoading) return

        setIsLoading(true)

        try {
            let res: any

            if (status === 'active') {
                setShowCancelationDialog(true)
                setIsLoading(false)
                return
            }
            // Expiring: update payment method via Stripe portal
            if (status === 'expiring') {
                await openPortal()
                setIsLoading(false)
                return
            }
            // For canceling, canceled, expired or not set: checkout
            const fullUrl = getFullUrl()
            res = await createCheckoutSessionAction(fullUrl, lang)
            if (res?.data?.redirect_url) {
                toast.success('You will be redirected to our paymentProvider.')
                router.push(res.data.redirect_url)
                return
            }
            if (!res) {
                throw new Error('No response received')
            }
            if (res && 'error' in res) {
                throw new Error(res.error)
            }
        } catch (error: any) {
            const errorMessage =
                error?.message ||
                (status === 'canceled'
                    ? errorResumingSubscription
                    : status === 'active'
                      ? errorSubscribing
                      : status !== 'active'
                        ? errorUnsubscribing
                        : 'Error')
            toast.error(errorMessage)
        } finally {
            setIsLoading(false)
        }
    }

    const handlePremiumSubscriptionToggle = async () => {
        if (!session?.user) {
            toast.error(notLoggedIn)
            const segments = pathname.split('/')
            const locale = segments[1]
            router.push(`/${locale}/login`)
            return
        }
        try {
            await togglePremiumSubscription(status)
        } catch (error) {
            toast.error('Something went wrong: ' + error)
        }
    }

    return (
        <div className='flex items-center justify-center '>
            <PremiumCancelationDialog
                premiumUnsubscribeConfirmationTitle={userPremiumUnsubscribeConfirmationTitle}
                premiumUnsubscribeConfirmationText={
                    areYouSureYouWantToCancelYourPremiumSubscriptionYouWillLoseAccessToAllPremiumFeaturesAtTheEndOfYourCurrentSubscriptionPeriod
                }
                premiumUnsubscribeConfirmationCancel={keepPremium}
                premiumUnsubscribeConfirmationConfirm={cancelPremium}
                unsubscribeSuccess={premiumSubscriptionCancelledSuccessfully}
                unsubscribeError={errorUnsubscribing}
                pathname={pathname}
                open={showCancelationDialog}
                onClose={() => setShowCancelationDialog(false)}
            />
            <Button
                onClick={handlePremiumSubscriptionToggle}
                className={`relative w-full font-semibold text-base py-2.5  whitespace-normal text-center ${
                    status !== 'active' ? '' : 'bg-destructive'
                }`}
                disabled={isLoading}
            >
                <div className='flex items-center'>
                    <span className={`${isLoading ? 'invisible' : ''}`}>{buttonText}</span>
                    {isLoading && <LoadingAnimation className='!size-5' />}
                </div>
            </Button>
        </div>
    )
}

export default PremiumToggleButton
